import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"

import { N0DES_API_PREFIX } from "@/api/domain"
import type { TraceEventsWithSummary } from "@/simulations"
import type { ProjectIdParam, Uuid } from "@/types"
import { Workflow } from "@/workflows"
import {
  SessionInfo,
  SessionOverview,
  TraceInfo,
  TraceSummary,
  TraceSummaryEvents,
} from "@n0computer/ui/src/types"

export type TraceData = {
  session_id: string
  name: string
  trace_id: string
  created_at: string
  updated_at: string
  status: TraceStatus
}

export enum TraceStatus {
  Pending = "Pending",
  Success = "Success",
  Failure = "Failure",
}

export const simulationsApi = createApi({
  reducerPath: "simulationsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${N0DES_API_PREFIX}simulations`,
    mode: "cors",
    credentials: "include",
  }),
  endpoints: (build) => ({
    getOverview: build.query<SessionOverview | null, ProjectIdParam>({
      query: ({ project_id }) => ({
        url: `${project_id}`,
      }),
    }),
    getTraceSummary: build.query<
      TraceSummary | null,
      ProjectIdParam & { trace_id: Uuid | null }
    >({
      query: ({ project_id, trace_id }) => ({
        url: `${project_id}/trace/${trace_id}/summary`,
      }),
    }),
    getTraceSummaryEvents: build.query<
      TraceSummaryEvents | null,
      ProjectIdParam & { trace_id: Uuid | null }
    >({
      query: ({ project_id, trace_id }) => ({
        url: `${project_id}/trace/${trace_id}/events`,
      }),
    }),
    getSessions: build.query<SessionInfo[], ProjectIdParam>({
      query: ({ project_id }) => ({
        url: `${project_id}/sessions`,
      }),
    }),
    getTraces: build.query<TraceData[], ProjectIdParam>({
      query: ({ project_id }) => ({
        url: `${project_id}/traces`,
      }),
    }),
    workflowPostSimulation: build.mutation<Workflow, ProjectIdParam>({
      query: (body) => ({
        url: `${body.project_id}`,
        method: "POST",
      }),
    }),
  }),
})

export const {
  useGetOverviewQuery,
  useGetTraceSummaryEventsQuery,
  useGetSessionsQuery,
  useGetTracesQuery,
  useGetTraceSummaryQuery,
  useWorkflowPostSimulationMutation,
} = simulationsApi
